/**
 * PwaInstallHint — friendly nudge to add the app to the home screen.
 *
 * Two surfaces:
 *   • Parent register form (variant "register"): one-shot with a permanent
 *     "don't ask again" close.
 *   • Kid home page (variant "kid"): re-nudges every few days (snooze
 *     model) with kid-friendly copy.
 *
 * Per-platform behaviour:
 *   • Already installed (display-mode standalone, or iOS standalone):
 *     renders nothing.
 *   • Chromium + beforeinstallprompt: shows an Install button that calls
 *     .prompt() directly.
 *   • iOS Safari: manual "Share → Add to Home Screen" instructions
 *     (Apple disallows beforeinstallprompt).
 *   • Anything else: a generic instruction line.
 *
 * Snooze model:
 *   localStorage `pwa.snoozeUntil` — timestamp; while now < it, hide.
 *   localStorage `pwa.dismissedForever` — '1' = never show again.
 *   "稍后再说" = snooze 3 days. "× close" = snooze 7 days. "不用了" =
 *   dismiss forever (kid variant doesn't expose this; the kid only gets
 *   the gentler buttons, parents can suppress for good).
 */

import { useEffect, useState } from 'react';
import { t, useLang } from '../i18n';

const SNOOZE_KEY = 'pwa.snoozeUntil';
const DISMISS_FOREVER_KEY = 'pwa.dismissedForever';
// Legacy key from before snoozing was added — interpret as "never show
// again" for back-compat with already-dismissed devices.
const LEGACY_DISMISS_KEY = 'pwa.dismissed';

const DAY_MS = 24 * 60 * 60 * 1000;
const SNOOZE_LATER_MS = 3 * DAY_MS;
const SNOOZE_CLOSE_MS = 7 * DAY_MS;

type InstallPlatform = 'installed' | 'chromium' | 'ios' | 'other';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface Props {
  /** 'kid' uses friendlier copy + snooze (default for child app contexts).
   *  'register' is the original one-shot card on the parent register form. */
  variant?: 'register' | 'kid';
}

function detectInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  if ((navigator as unknown as { standalone?: boolean }).standalone) return true;
  if (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches) return true;
  return false;
}

function detectIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/.test(ua)) return true;
  if (/Mac/.test(ua) && (navigator.maxTouchPoints || 0) > 1) return true;
  return false;
}

function isSnoozed(): boolean {
  try {
    if (localStorage.getItem(DISMISS_FOREVER_KEY) === '1') return true;
    if (localStorage.getItem(LEGACY_DISMISS_KEY) === '1') return true;
    const until = Number(localStorage.getItem(SNOOZE_KEY) || '0');
    if (until > Date.now()) return true;
  } catch { /* ignore */ }
  return false;
}

function snooze(ms: number): void {
  try { localStorage.setItem(SNOOZE_KEY, String(Date.now() + ms)); }
  catch { /* ignore */ }
}

function dismissForever(): void {
  try { localStorage.setItem(DISMISS_FOREVER_KEY, '1'); }
  catch { /* ignore */ }
}

export default function PwaInstallHint({ variant = 'register' }: Props = {}) {
  useLang();
  const [platform, setPlatform] = useState<InstallPlatform>('other');
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [hidden, setHidden] = useState<boolean>(() => isSnoozed());

  useEffect(() => {
    if (detectInstalled()) { setPlatform('installed'); return; }
    if (detectIos()) { setPlatform('ios'); return; }

    const handler = (e: Event) => {
      e.preventDefault();
      setInstallEvent(e as BeforeInstallPromptEvent);
      setPlatform('chromium');
    };
    window.addEventListener('beforeinstallprompt', handler);
    const installed = () => setPlatform('installed');
    window.addEventListener('appinstalled', installed);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installed);
    };
  }, []);

  function close() {
    snooze(SNOOZE_CLOSE_MS);
    setHidden(true);
  }
  function later() {
    snooze(SNOOZE_LATER_MS);
    setHidden(true);
  }
  function neverAgain() {
    dismissForever();
    setHidden(true);
  }

  async function triggerInstall() {
    if (!installEvent) return;
    try {
      await installEvent.prompt();
      const choice = await installEvent.userChoice;
      if (choice.outcome === 'accepted') {
        setPlatform('installed');
      }
    } catch { /* user cancelled */ }
    setInstallEvent(null);
  }

  if (platform === 'installed') return null;
  if (hidden) return null;

  const isKid = variant === 'kid';

  return (
    <div
      className="card"
      style={{
        padding: isKid ? '16px 16px 14px' : '12px 14px',
        background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
        border: '1px solid rgba(217, 119, 6, 0.35)',
        marginBottom: 14,
        position: 'relative',
      }}
    >
      <button
        onClick={close}
        aria-label={t('common.close')}
        style={{
          position: 'absolute', top: 6, right: 8,
          background: 'transparent', border: 'none', cursor: 'pointer',
          fontSize: 18, color: 'rgba(120, 53, 15, 0.6)',
          padding: 4, lineHeight: 1,
        }}
      >×</button>

      <div style={{ fontWeight: 800, fontSize: isKid ? 16 : 14, color: '#78350f', marginBottom: 6 }}>
        ✨ {t(isKid ? 'pwa.hint.kid.title' : 'pwa.hint.title')}
      </div>

      {platform === 'chromium' && (
        <>
          <div style={{ fontSize: 13, lineHeight: 1.5, color: '#78350f' }}>
            {t(isKid ? 'pwa.hint.kid.chromium.body' : 'pwa.hint.chromium.body')}
          </div>
          <button
            type="button"
            className="btn btn-primary"
            style={{ marginTop: 10, padding: '10px 18px', fontSize: 14 }}
            onClick={triggerInstall}
          >
            📥 {t('pwa.hint.chromium.cta')}
          </button>
        </>
      )}

      {platform === 'ios' && (
        <ol
          style={{
            fontSize: 13, lineHeight: 1.7, color: '#78350f',
            paddingLeft: 22, margin: '4px 0 0',
          }}
        >
          <li>{t(isKid ? 'pwa.hint.kid.ios.step1' : 'pwa.hint.ios.line1')}</li>
          <li>{t(isKid ? 'pwa.hint.kid.ios.step2' : 'pwa.hint.ios.line2')}</li>
          {isKid && <li>{t('pwa.hint.kid.ios.step3')}</li>}
        </ol>
      )}

      {platform === 'other' && (
        <div style={{ fontSize: 13, lineHeight: 1.5, color: '#78350f' }}>
          {t(isKid ? 'pwa.hint.kid.other.body' : 'pwa.hint.other.body')}
        </div>
      )}

      {/* Kid variant gets two soft buttons; parent register variant relies
          on the × in the corner. */}
      {isKid && (
        <div className="flex" style={{ marginTop: 12, gap: 8 }}>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ flex: 1, fontSize: 13 }}
            onClick={later}
          >
            {t('pwa.hint.kid.later')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ flex: 1, fontSize: 13, opacity: 0.85 }}
            onClick={neverAgain}
          >
            {t('pwa.hint.kid.neverAgain')}
          </button>
        </div>
      )}
    </div>
  );
}
